import { defineStore } from 'pinia';
import { ref } from 'vue';
import { settingsStore } from '@/services/settingsStore';

const PREFERENCES_TYPE = 'userPreference' as const;
const PREFERENCES_KEY = 'preferences';
const WORKOUT_FUEL_MODE_KEY = 'planning:workoutFuelMode';
const LEGACY_DIARY_SETTING_KEYS = [
  'diary:showDayTiles',
  'diary:showSummary',
  'diary:showMealPlanning',
] as const;

export type WorkoutFuelMode = 'carbsPerHour' | 'calories';
type PreferencesRecord = Record<string, unknown>;

const readPreferences = async (): Promise<PreferencesRecord> => {
  const preferences = await settingsStore.get<PreferencesRecord>(PREFERENCES_TYPE, PREFERENCES_KEY, {});
  if (!preferences || typeof preferences !== 'object' || Array.isArray(preferences)) {
    return {};
  }
  return preferences;
};

const readLegacySetting = async (
  key: string,
  defaultValue: boolean,
): Promise<{ value: boolean; exists: boolean }> => {
  const settingId = `${PREFERENCES_TYPE}:${key}`;
  const legacyRecord = await settingsStore.storage.getById(settingId);
  if (!legacyRecord) {
    return { value: defaultValue, exists: false };
  }

  return {
    value: typeof legacyRecord.value === 'boolean' ? legacyRecord.value : defaultValue,
    exists: true,
  };
};

const readBooleanPreference = (
  preferences: PreferencesRecord,
  key: string,
  fallback: boolean,
): boolean => {
  const value = preferences[key];
  return typeof value === 'boolean' ? value : fallback;
};

const readWorkoutFuelModePreference = (
  preferences: PreferencesRecord,
): WorkoutFuelMode => {
  const value = preferences[WORKOUT_FUEL_MODE_KEY];
  return value === 'calories' || value === 'carbsPerHour' ? value : 'carbsPerHour';
};

export const useDiarySettingsStore = defineStore('diarySettings', () => {
  const showDayTiles = ref(true);
  const showSummary = ref(true);
  const showMealPlanning = ref(true);
  const workoutFuelMode = ref<WorkoutFuelMode>('carbsPerHour');
  const isLoaded = ref(false);

  async function load(): Promise<void> {
    if (isLoaded.value) return;

    const preferences = await readPreferences();

    const legacyDayTiles = await readLegacySetting('diary:showDayTiles', true);
    const legacySummary = await readLegacySetting('diary:showSummary', true);
    const legacyMealPlanning = await readLegacySetting('diary:showMealPlanning', true);

    const resolvedShowDayTiles = readBooleanPreference(
      preferences,
      'diary:showDayTiles',
      legacyDayTiles.value,
    );
    const resolvedShowSummary = readBooleanPreference(
      preferences,
      'diary:showSummary',
      legacySummary.value,
    );
    const resolvedShowMealPlanning = readBooleanPreference(
      preferences,
      'diary:showMealPlanning',
      legacyMealPlanning.value,
    );
    const resolvedWorkoutFuelMode = readWorkoutFuelModePreference(preferences);

    showDayTiles.value = resolvedShowDayTiles;
    showSummary.value = resolvedShowSummary;
    showMealPlanning.value = resolvedShowMealPlanning;
    workoutFuelMode.value = resolvedWorkoutFuelMode;

    const nextPreferences: PreferencesRecord = {
      ...preferences,
      'diary:showDayTiles': resolvedShowDayTiles,
      'diary:showSummary': resolvedShowSummary,
      'diary:showMealPlanning': resolvedShowMealPlanning,
      [WORKOUT_FUEL_MODE_KEY]: resolvedWorkoutFuelMode,
    };

    const didMigrateLegacy =
      legacyDayTiles.exists ||
      legacySummary.exists ||
      legacyMealPlanning.exists;
    const preferencesChanged =
      preferences['diary:showDayTiles'] !== resolvedShowDayTiles ||
      preferences['diary:showSummary'] !== resolvedShowSummary ||
      preferences['diary:showMealPlanning'] !== resolvedShowMealPlanning ||
      preferences[WORKOUT_FUEL_MODE_KEY] !== resolvedWorkoutFuelMode;

    if (didMigrateLegacy || preferencesChanged) {
      await settingsStore.save(PREFERENCES_TYPE, PREFERENCES_KEY, nextPreferences);
    }

    if (didMigrateLegacy) {
      for (const key of LEGACY_DIARY_SETTING_KEYS) {
        await settingsStore.storage.removeFromSync(`${PREFERENCES_TYPE}:${key}`);
      }
    }

    isLoaded.value = true;
  }

  async function reload(): Promise<void> {
    isLoaded.value = false;
    await load();
  }

  async function setShowDayTiles(v: boolean): Promise<void> {
    showDayTiles.value = v;
    const preferences = await readPreferences();
    await settingsStore.save(PREFERENCES_TYPE, PREFERENCES_KEY, {
      ...preferences,
      'diary:showDayTiles': v,
    });
  }

  async function setShowSummary(v: boolean): Promise<void> {
    showSummary.value = v;
    const preferences = await readPreferences();
    await settingsStore.save(PREFERENCES_TYPE, PREFERENCES_KEY, {
      ...preferences,
      'diary:showSummary': v,
    });
  }

  async function setShowMealPlanning(v: boolean): Promise<void> {
    showMealPlanning.value = v;
    const preferences = await readPreferences();
    await settingsStore.save(PREFERENCES_TYPE, PREFERENCES_KEY, {
      ...preferences,
      'diary:showMealPlanning': v,
    });
  }

  async function setWorkoutFuelMode(v: WorkoutFuelMode): Promise<void> {
    workoutFuelMode.value = v;
    const preferences = await readPreferences();
    await settingsStore.save(PREFERENCES_TYPE, PREFERENCES_KEY, {
      ...preferences,
      [WORKOUT_FUEL_MODE_KEY]: v,
    });
  }

  return {
    showDayTiles,
    showSummary,
    showMealPlanning,
    workoutFuelMode,
    isLoaded,
    load,
    reload,
    setShowDayTiles,
    setShowSummary,
    setShowMealPlanning,
    setWorkoutFuelMode,
  };
});
